<template lang="pug">
.pack-search-view
  shLoader(:show="loading")

  template(v-if="!loading && record && record.data")
    PackRecordProducts(
      :data-original="record.data"
      :record-type="record.type"
    )

    .pack-search-view__actions(v-if="record && record.data")
      .order-actions-container
        .order-actions.relative
          .undo(v-if="detailAction && detailAction.previousAction")
            img(
              :src="require('@/assets/undo.svg')"
              :alt="`undo-${record.data.status}`"
              :class="{ disabled: detailActionLoading }"
              @click="undoDetailAction"
            )
          .undo(v-else)
          .buttons-actions
            shActionButton(
              v-if="detailAction && detailAction.nextAction"
              :action="detailAction.nextAction"
              :params="detailActionParams"
              :text="detailSendActionText"
              :textRunning="detailSendActionTextRunning"
              :textConfirmation="detailSendConfirmation"
              width="9rem"
            )
            SendSkipGuideToggle(v-if="canSkipGuide" v-model="skipGuide")
            shActionButton(
              :action="generateRemit"
              icon="article"
              text="Generar remite"
              textRunning="Generando remite"
              :normalColor="true"
            )
    .pack-search-view__fraud-block.is-error(v-if="detailPackBlockMessage") {{ detailPackBlockMessage }}
</template>

<script>
import gql from '@my/graphql'
import gqlOrder from '@/graphql/order'
import gqlChange from '@/graphql/change'
import modals from '@my/store/modals'
import shLoader from '@/components/layout/Loader'
import shActionButton from '@/components/elements/ActionButton'
import SendSkipGuideToggle from '@/components/elements/order/SendSkipGuideToggle.vue'
import PackRecordProducts from '@/components/pages/order/pack/PackRecordProducts.vue'
import { isBlockedByFraud } from '@/helpers/fraudReview'
import { isBlockedByProductChange, isBlockedByAddressNovelty, orderPackBlockMessage, orderSendBlockMessage } from '@/helpers/orderNovelty'

export default {
  name: 'PackSearchView',

  components: {
    shLoader,
    shActionButton,
    SendSkipGuideToggle,
    PackRecordProducts
  },

  props: {
    record: {
      type: Object,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      detailActionLoading: false,
      skipGuide: false
    }
  },

  watch: {
    'record.data.order' () {
      this.skipGuide = false
    },
    'record.data.change' () {
      this.skipGuide = false
    }
  },

  computed: {
    detailAction () {
      if (!this.record || !this.record.data) return null

      const actions = this.record.type === 'order'
        ? {
          paid: {
            previousAction: null,
            nextAction: this.packOrder,
            text: 'Empacar',
            textRunning: 'Empacando'
          },
          packed: {
            previousAction: this.unpackOrder,
            nextAction: this.sendOrder,
            text: 'Enviar',
            textRunning: 'Enviando',
            textConfirmation: '¿Deseas enviar la orden?'
          },
          sent: {
            previousAction: this.backSendOrder,
            nextAction: null,
            text: '',
            textRunning: ''
          }
        }
        : {
          paid: {
            previousAction: null,
            nextAction: this.packChange,
            text: 'Empacar',
            textRunning: 'Empacando'
          },
          packed: {
            previousAction: this.unpackChange,
            nextAction: this.sendChange,
            text: 'Enviar',
            textRunning: 'Enviando',
            textConfirmation: '¿Deseas enviar el cambio?'
          },
          sent: {
            previousAction: this.backSendChange,
            nextAction: null,
            text: '',
            textRunning: ''
          }
        }

      const action = actions[this.record.data.status] || null
      if (!action || this.record.type !== 'order') return action
      const isBlocked = this.record.data.status === 'packed'
        ? (isBlockedByFraud(this.record.data) || isBlockedByAddressNovelty(this.record.data))
        : (isBlockedByFraud(this.record.data) || isBlockedByProductChange(this.record.data))
      if (isBlocked) {
        return {
          ...action,
          nextAction: null
        }
      }
      return action
    },

    detailPackBlockMessage () {
      if (!this.record || this.record.type !== 'order' || !this.record.data) return ''
      if (this.record.data.status === 'packed') return orderSendBlockMessage(this.record.data)
      return orderPackBlockMessage(this.record.data)
    },

    detailActionParams () {
      if (!this.record || !this.record.data) return []

      return this.record.type === 'order'
        ? [this.record.data.order]
        : [this.record.data.change]
    },

    canSkipGuide () {
      return !!(this.record && this.record.data && this.record.data.status === 'packed' && this.detailAction && this.detailAction.nextAction)
    },

    detailSendActionText () {
      if (this.canSkipGuide && this.skipGuide) return 'E. Sin Guía'
      return this.detailAction && this.detailAction.text
    },

    detailSendActionTextRunning () {
      if (this.canSkipGuide && this.skipGuide) return 'Enviando sin guía'
      return this.detailAction && this.detailAction.textRunning
    },

    detailSendConfirmation () {
      if (!this.detailAction) return ''
      if (!this.record || !this.record.data || this.record.data.status !== 'packed') return this.detailAction.textConfirmation
      if (!this.skipGuide) return this.detailAction.textConfirmation
      return this.record.type === 'change'
        ? '¿Deseas enviar el cambio sin guía?'
        : '¿Deseas enviar la orden sin guía?'
    }
  },

  methods: {
    async undoDetailAction () {
      if (this.detailActionLoading || !this.detailAction || !this.detailAction.previousAction) return

      this.detailActionLoading = true

      try {
        await this.detailAction.previousAction(...this.detailActionParams)
      } finally {
        setTimeout(() => {
          this.detailActionLoading = false
        }, 500)
      }
    },

    async generateRemit () {
      if (!this.record || !this.record.data) return

      const variables = this.record.type === 'order'
        ? { order: this.record.data.order }
        : { change: this.record.data.change }

      const errors = []
      const response = await gql.mutate(
        gqlOrder.GENERATE_REMIT_DATA,
        variables,
        false,
        errors
      )

      if (!response || response.error) {
        modals.showMessage({
          message: this.formatRemitError((response && response.error) || errors[0]),
          type: 'error'
        })
        return
      }

      const result = response.data.generateRemitData

      if (!result || !result.success) {
        modals.showMessage({
          message: (result && result.message) || 'Error al generar remite',
          type: 'error'
        })
        return
      }

      modals.showMessage({ message: result.message || 'Remite generado' })
    },

    formatRemitError (error) {
      if (!error) return 'No hubo respuesta del servidor'
      if (typeof error === 'string') return error
      if (error.message) return error.message
      return String(error)
    },

    async packOrder (order) {
      if (!order) return

      const response = await gql.mutate(gqlOrder.PACK, { order })

      if (response) {
        this.$set(this.record.data, 'status', 'packed')
        modals.showMessage({ message: `Se empacó la orden ${order} correctamente` })
      }
    },

    async sendOrder (order) {
      if (!order) return

      const response = await gql.mutate(gqlOrder.SEND, { order, skipGuide: this.skipGuide })

      if (response) {
        this.$set(this.record.data, 'status', 'sent')
        modals.showMessage({ message: `Se envió la orden ${order} correctamente` })
      }
    },

    async unpackOrder (order) {
      if (!order) return

      const response = await gql.mutate(gqlOrder.UNPACK, { order })

      if (response) {
        this.$set(this.record.data, 'status', 'paid')
        modals.showMessage({ message: `Se desempacó la orden ${order} correctamente` })
      }
    },

    async backSendOrder (order) {
      if (!order) return

      const response = await gql.mutate(gqlOrder.BACK_SEND, { order })

      if (response) {
        this.$set(this.record.data, 'status', 'packed')
        modals.showMessage({ message: `Se devolvió el envío de la orden ${order} correctamente` })
      }
    },

    async packChange (change) {
      if (!change) return

      const response = await gql.mutate(gqlChange.PACK, { change, sendNotifications: false })

      if (response) {
        this.$set(this.record.data, 'status', 'packed')
        modals.showMessage({ message: `Se empacó el cambio ${change} correctamente` })
      }
    },

    async sendChange (change) {
      if (!change) return

      const response = await gql.mutate(gqlChange.SEND, { change, sendNotifications: false, skipGuide: this.skipGuide })

      if (response) {
        this.$set(this.record.data, 'status', 'sent')
        modals.showMessage({ message: `Se envió el cambio ${change} correctamente` })
      }
    },

    async unpackChange (change) {
      if (!change) return

      const response = await gql.mutate(gqlChange.UNPACK, { change })

      if (response) {
        this.$set(this.record.data, 'status', 'paid')
        modals.showMessage({ message: `Se desempacó el cambio ${change} correctamente` })
      }
    },

    async backSendChange (change) {
      if (!change) return

      const response = await gql.mutate(gqlChange.BACK_SEND, { change })

      if (response) {
        this.$set(this.record.data, 'status', 'packed')
        modals.showMessage({ message: `Se devolvió el envío del cambio ${change} correctamente` })
      }
    }
  }
}
</script>

<style lang="stylus" scoped>
.pack-search-view
  padding 0 .3rem 2rem

.pack-search-view__actions
  margin 2rem 0 1rem
  width 100%

.pack-search-view__fraud-block
  margin-top 1rem
  font-size .95rem
  color var(--color-red)

@media (min-width: 769px)
  .pack-search-view__actions
    display none
</style>



// WEBPACK FOOTER //
// src/components/pages/order/pack/PackSearchView.vue